import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Plus, Building2, Trash2, Loader2 } from "lucide-react";

export default function AdminInstitutions() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [desc, setDesc] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/institutions").then((r) => setItems(r.data));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await api.post("/admin/institutions", { name, code, description: desc });
      setName("");
      setCode("");
      setDesc("");
      setShowNew(false);
      load();
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this institution?")) return;
    await api.delete(`/admin/institutions/${id}`);
    load();
  };

  return (
    <AppShell title="Institutions" subtitle="Onboard schools and colleges as tenants.">
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowNew(!showNew)} className="btn-primary text-sm" data-testid="new-institution-toggle">
          <Plus className="w-4 h-4" /> Onboard institution
        </button>
      </div>
      {showNew && (
        <form onSubmit={create} className="qs-card p-6 mb-6" data-testid="new-institution-form">
          <div className="grid md:grid-cols-2 gap-3">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Institution name"
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="new-institution-name"
            />
            <input
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Unique code (e.g. GHS-02)"
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
              data-testid="new-institution-code"
            />
          </div>
          <textarea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            className="w-full mt-3 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500 outline-none"
            data-testid="new-institution-desc"
          />
          {err && <p className="mt-2 text-sm text-rose-600">{err}</p>}
          <button type="submit" disabled={busy} className="btn-primary text-sm mt-3" data-testid="new-institution-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
          </button>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="institution-list">
        {items.map((inst) => (
          <div key={inst.id} className="qs-card p-5" data-testid={`institution-card-${inst.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-11 h-11 rounded-xl bg-violet-50 text-violet-600 grid place-items-center">
                <Building2 className="w-5 h-5" />
              </div>
              <button onClick={() => del(inst.id)} className="text-rose-500" data-testid={`delete-institution-${inst.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <h3 className="mt-4 font-display text-lg font-bold text-slate-900">{inst.name}</h3>
            <p className="text-xs text-slate-500">Code: {inst.code}</p>
            <p className="mt-2 text-sm text-slate-600 line-clamp-2">{inst.description}</p>
          </div>
        ))}
        {items.length === 0 && (
          <p className="col-span-full text-center text-slate-500 py-12" data-testid="no-institutions">No institutions yet.</p>
        )}
      </div>
    </AppShell>
  );
}
